import { useState } from 'react';

import { useAppState } from '../AppStateProvider';
import { Header } from '../components/Header';
import { SearchTextBox } from '../components/SearchTextBox';
import type { Contact } from '../domain/contact';
import { useLinkedinContactsListQuery } from '../features/contacts/linkedinContactsList/useLinkedinContactsListQuery';
import { useContactSearch } from '../hooks/useContactSearch';
import { translate } from '../services/i18n';

import { BulkActionBar } from './contacts/BulkActionBar';
import { ContactList } from './contacts/ContactList';
import { StyledContactsHeader, StyledContactsHeaderContainer } from './contacts/styles';
import { StyledContainer, StyledPageHeading, StyledSearchContainer } from './styles';

export const SearchPage = () => {
  const { data, isLoading, isError } = useLinkedinContactsListQuery();
  const { searchText, setSearchText, filteredContacts } = useContactSearch(data);
  const { searchPagePagination: pagination } = useAppState();

  const [shouldAnimatePrivateDataMount, setShouldAnimatePrivateDataMount] = useState(false);

  const shouldShowScrollInfo = () => {
    if (isLoading || isError) {
      return false;
    }
    if (!data?.length) {
      return false;
    }
    if (pagination.total === 0) {
      return false;
    }
    if (data.length === pagination.pageSize) {
      return false;
    }

    const isLastPage = pagination.page === Math.ceil(pagination.total / pagination.pageSize);
    const recordsOnTheLastPage = pagination.total - (pagination.page - 1) * pagination.pageSize;
    if (isLastPage && recordsOnTheLastPage === data.length) {
      return false;
    }

    return true;
  };

  return (
    <>
      <Header />
      <StyledContainer>
        <StyledSearchContainer data-automation="SearchPage">
          <StyledContactsHeaderContainer>
            <StyledContactsHeader>
              <StyledPageHeading data-automation="SearchPageHeader">{translate('header.search')}</StyledPageHeading>
              <SearchTextBox
                searchText={searchText}
                onChange={setSearchText}
                placeholder={translate('contacts.search_placeholder')}
                isLoading={isLoading}
              />
            </StyledContactsHeader>
            <BulkActionBar
              contacts={filteredContacts as Contact[]}
              setShouldAnimatePrivateDataMount={setShouldAnimatePrivateDataMount}
            />
          </StyledContactsHeaderContainer>
          <ContactList
            data={filteredContacts as Contact[]}
            isError={isError}
            withCompanyName
            showScrollInfo={shouldShowScrollInfo()}
            shouldAnimatePrivateDataMount={shouldAnimatePrivateDataMount}
          />
        </StyledSearchContainer>
      </StyledContainer>
    </>
  );
};
